import type { Schema, ValidationError } from 'yup'
import * as v from 'valibot'
import { setIn } from 'final-form'

type FinalFormErrorTree<FormData> = FormData extends object
  ? { [K in keyof FormData]?: FinalFormErrorTree<FormData[K]> }
  : FormData extends Array<infer ArrayItem>
    ? FinalFormErrorTree<ArrayItem>[]
    : string

export const getFinalFormErrorTree = <FormData extends object>(
  validationError: { message: string; path: string }[]
) => {
  return validationError.reduce((formErrorTree, error) => {
    return setIn(formErrorTree, error.path, error.message) as FinalFormErrorTree<FormData>
  }, {} as FinalFormErrorTree<FormData>)
}

// https://gist.github.com/manzoorwanijk/5993a520f2ac7890c3b46f70f6818e0a
export const createYupValidateFinalFormValues =
  <T extends object, S extends Schema<unknown> = Schema<unknown>>(schema: S) =>
  async (values: T) => {
    try {
      await schema.validate(values, { abortEarly: false })
      return true
    } catch (err) {
      const { inner } = err as ValidationError
      return getFinalFormErrorTree<T>(inner)
    }
  }

export const createValibotValidateFinalFormValues = <
  T extends object,
  S extends v.GenericSchema<unknown, T>,
>(
  schema: S
) => {
  return async (values: T) => {
    const result = v.safeParse(schema, values)
    if (result.success) {
      return true
    }
    return result.issues.reduce((acc, issue) => {
      const path = issue.path?.map(p => p.key).join('.')
      if (!path) {
        throw new Error('Path must be defined when validating an object')
      }
      return setIn(acc, path, issue.message) as FinalFormErrorTree<T>
    }, {} as FinalFormErrorTree<T>)
  }
}
